/**
 * Stable dynamic pagination for product, collection and article grids.
 * It keeps the existing theme markup contract:
 * - #product-grid / #collection-grid / #article-grid
 * - .js-btn-load-more with data-href
 * - .js-btn-load-previous with data-href
 */
class DynamicLoad extends HTMLElement {
  constructor() {
    super();

    this.itemGrid =
      document.querySelector("#product-grid") ||
      document.querySelector("#collection-grid") ||
      document.querySelector("#article-grid");

    if (!this.itemGrid) {
      console.warn("Dynamic load grid element was not found.");
      return;
    }

    this.gridType = this.getGridType();
  }

  getGridType() {
    if (this.itemGrid.id === "collection-grid") return "collection";
    if (this.itemGrid.id === "article-grid") return "article";
    return "product";
  }

  getGridId() {
    return `${this.gridType}-grid`;
  }

  getContainerId() {
    if (this.gridType === "collection") return "CollectionGridContainer";
    if (this.gridType === "article") return "ArticleGridContainer";
    return "ProductGridContainer";
  }

  getItemSelector() {
    if (this.gridType === "collection") return ".card-collection";
    if (this.gridType === "article") return ".card-article";
    return ".product-card";
  }

  /**
   * "Bu sayfada icerik var mi?" kontrolu icin kullanilan secici.
   *
   * getItemSelector() urun sayacini besledigi icin yalnizca urun kartlarini
   * saymali. Ama arama sonuclari urun disi sonuc da icerebiliyor (sayfa ve
   * blog yazilari card-result olarak basiliyor). Onlari hesaba katmazsak
   * sadece blog sonucu iceren bir sayfayi "bos" sanip atlar ve o sonuclari
   * sessizce kaybederdik.
   */
  getContentSelector() {
    if (this.gridType === "collection") return ".card-collection";
    if (this.gridType === "article") return ".card-article";
    return ".product-card, .card-result";
  }

  getContainer() {
    return document.querySelector(`#${this.getContainerId()}`);
  }

  getSectionId() {
    const container = this.getContainer();
    return container ? container.dataset.sectionId || "" : "";
  }

  buildRequestUrl(href) {
    const url = new URL(href, window.location.origin);
    const sectionId = this.getSectionId();

    if (sectionId) {
      url.searchParams.set("sections", sectionId);
    }

    return url.toString();
  }

  async readResponseHtml(response) {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const sectionId = this.getSectionId();
      const sections = await response.json();
      return sectionId && sections[sectionId] ? sections[sectionId] : "";
    }

    return response.text();
  }

  /**
   * Yeni sayfanin kartlarini tasiyarak ekler.
   *
   * Onceki surum `grid.innerHTML` ile parse edilmis DOM'u tekrar string'e
   * cevirip insertAdjacentHTML ile yeniden parse ediyordu; yani her yuklemede
   * ayni HTML iki kez parse ediliyor ve 24 kartlik buyuk bir gecici string
   * uretiliyordu. Dugumleri dogrudan tasimak bu iki isi de ortadan kaldiriyor.
   * Ozel elementler (product-card) dokumana eklendikleri anda zaten upgrade
   * edildigi icin davranis aynidir.
   */
  appendContent(sourceGrid, prepend = false) {
    if (!sourceGrid || !sourceGrid.firstChild) return;

    const fragment = document.createDocumentFragment();
    while (sourceGrid.firstChild) {
      fragment.appendChild(sourceGrid.firstChild);
    }

    if (prepend) {
      this.itemGrid.insertBefore(fragment, this.itemGrid.firstChild);
    } else {
      this.itemGrid.appendChild(fragment);
    }

    document.dispatchEvent(new CustomEvent("ahel:dynamic-load"));
  }

  updateUrlFromHref(href) {
    if (!href) return;

    const loadedUrl = new URL(href, window.location.origin);
    const loadedPage = loadedUrl.searchParams.get("page");
    const currentUrl = new URL(window.location.href);

    if (loadedPage && loadedPage !== "1") {
      currentUrl.searchParams.set("page", loadedPage);
    } else {
      currentUrl.searchParams.delete("page");
    }

    history.replaceState({}, "", currentUrl.toString());
  }

  updateItemCount() {
    if (this.gridType !== "product") return;

    const itemCountOfAll = document.querySelector(
      ".collection-facets__product-count .product-count-of-all"
    );

    if (!itemCountOfAll) return;

    itemCountOfAll.innerText = this.itemGrid.querySelectorAll(this.getItemSelector()).length;
  }

  parsePage(htmlText) {
    // DOMParser her cagrida tam bir Document (head/body/stil hesabi dahil)
    // kuruyordu. <template> ayni fragmani cok daha ucuza parse eder ve
    // icindeki gorseller/iframe'ler kesinlikle istek atmaz.
    const holder = document.createElement("template");
    holder.innerHTML = htmlText;

    const parsedPage = holder.content;
    const container = parsedPage.querySelector(`#${this.getContainerId()}`);
    const grid = container ? container.querySelector(`#${this.getGridId()}`) : null;
    const nextButton = container ? container.querySelector(".js-btn-load-more") : null;
    const previousButton = container ? container.querySelector(".js-btn-load-previous") : null;

    return {
      grid,
      nextHref: nextButton ? nextButton.dataset.href || "" : "",
      previousHref: previousButton ? previousButton.dataset.href || "" : "",
      itemCount: grid ? grid.querySelectorAll(this.getContentSelector()).length : 0,
    };
  }

  isIOS() {
    const platform = navigator.platform || "";
    const userAgent = navigator.userAgent || "";
    const hasTouch = navigator.maxTouchPoints && navigator.maxTouchPoints > 1;

    return /iPad|iPhone|iPod/.test(userAgent) || (platform === "MacIntel" && hasTouch);
  }
}

if (!customElements.get("dynamic-load-more")) {
  class DynamicLoadMore extends DynamicLoad {
    constructor() {
      super();

      this.btnLoadMore = this.querySelector(".js-btn-load-more");
      this.observer = null;
      this.isLoading = false;

      // Tema stok disi urunleri render ederken atliyor (main-search.liquid,
      // main-collection.liquid) ama Shopify sayfalamayi onlar da dahilken
      // hesapliyor. Bu yuzden tamamen bos sayfalar olusabiliyor -- ozellikle
      // fiyata gore siralamada, stok disi urunler listenin bir ucunda
      // kumelendigi icin arka arkaya bircok bos sayfa cikabiliyor.
      // Bu deger, tek bir tetiklemede kac bos sayfanin atlanacagini sinirlar;
      // sinira takilirsak durmuyoruz, bir sonraki tetiklemede devam ediyoruz.
      this.maxEmptyPageFetches = 5;

      if (!this.itemGrid || !this.btnLoadMore) return;

      this.onClick = this.onClick.bind(this);
      this.btnLoadMore.addEventListener("click", this.onClick);

      this.updateItemCount();
      this.toggleButtonVisibility();
      this.setupInfinityScroll();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.btnLoadMore) this.btnLoadMore.removeEventListener("click", this.onClick);
    }

    onClick(event) {
      event.preventDefault();
      this.loadMoreItems();
    }

    isInfinityScrollEnabled() {
      const container = this.itemGrid.closest(`#${this.getContainerId()}`);
      return Boolean(container && container.classList.contains("infinity-scroll-active"));
    }

    setupInfinityScroll() {
      if (!this.isInfinityScrollEnabled() || !this.btnLoadMore || !this.btnLoadMore.dataset.href) {
        return;
      }

      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver(
        (entries) => {
          const shouldLoad = entries.some((entry) => entry.isIntersecting);
          if (shouldLoad && !this.isLoading && this.btnLoadMore.dataset.href) {
            this.loadMoreItems();
          }
        },
        {
          root: null,
          rootMargin: "160px 0px",
          threshold: 0,
        }
      );

      this.observer.observe(this.btnLoadMore);
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.btnLoadMore.toggleAttribute("disabled", isLoading);

      if (this.observer && isLoading) {
        this.observer.unobserve(this.btnLoadMore);
      }
    }

    toggleButtonVisibility() {
      const hasNextPage = Boolean(this.btnLoadMore && this.btnLoadMore.dataset.href);
      this.classList.toggle("hidden", !hasNextPage);
      this.btnLoadMore.style.display = hasNextPage ? "" : "none";
    }

    async loadMoreItems() {
      if (this.isLoading || !this.btnLoadMore.dataset.href) return;

      this.setLoading(true);

      let nextHref = this.btnLoadMore.dataset.href;
      let emptyPagesFetched = 0;
      let lastLoadedHref = "";

      try {
        while (nextHref) {
          lastLoadedHref = nextHref;

          const response = await fetch(this.buildRequestUrl(nextHref), {
            credentials: "same-origin",
            headers: { "X-Requested-With": "XMLHttpRequest" },
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const page = this.parsePage(await this.readResponseHtml(response));

          if (!page.grid) {
            console.warn(`Dynamic load grid #${this.getGridId()} was not found on ${nextHref}.`);
            nextHref = "";
            break;
          }

          if (page.itemCount > 0) {
            this.appendContent(page.grid);
            nextHref = page.nextHref;
            break;
          }

          // Bu sayfada gorunur sonuc yok; bir sonrakine gecip aramaya devam et.
          emptyPagesFetched += 1;
          nextHref = page.nextHref;

          if (emptyPagesFetched >= this.maxEmptyPageFetches) {
            // Tur basina istek sayisini siniriyoruz ama nextHref'i KORUYORUZ:
            // liste bitmedi, sadece bu turda yeterince ilerledik. Buton gorunur
            // kalir ve bir sonraki tetikleme kaldigimiz yerden devam eder.
            //
            // Onceki surum burada nextHref'i siliyordu; yani tek bir bos sayfa
            // butonu gizleyip sonsuz kaydirmayi kalici olarak olduruyordu.
            break;
          }
        }

        this.btnLoadMore.dataset.href = nextHref || "";
        this.updateUrlFromHref(lastLoadedHref);
        this.updateItemCount();
        this.toggleButtonVisibility();
      } catch (error) {
        console.error("Load more error:", error);
      } finally {
        this.setLoading(false);

        if (this.btnLoadMore.dataset.href && this.isInfinityScrollEnabled()) {
          this.setupInfinityScroll();
        }
      }
    }
  }

  customElements.define("dynamic-load-more", DynamicLoadMore);
}

if (!customElements.get("dynamic-load-previous")) {
  class DynamicLoadPrevious extends DynamicLoad {
    constructor() {
      super();

      this.btnLoadPrevious = this.querySelector(".js-btn-load-previous");
      this.isLoading = false;

      if (!this.itemGrid || !this.btnLoadPrevious) return;

      this.onClick = this.onClick.bind(this);
      this.btnLoadPrevious.addEventListener("click", this.onClick);

      this.updateItemCount();
      this.toggleButtonVisibility();
    }

    disconnectedCallback() {
      if (this.btnLoadPrevious) this.btnLoadPrevious.removeEventListener("click", this.onClick);
    }

    onClick(event) {
      event.preventDefault();
      this.loadPreviousItems();
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.btnLoadPrevious.toggleAttribute("disabled", isLoading);
    }

    toggleButtonVisibility() {
      const hasPreviousPage = Boolean(this.btnLoadPrevious && this.btnLoadPrevious.dataset.href);
      this.classList.toggle("hidden", !hasPreviousPage);
      this.btnLoadPrevious.style.display = hasPreviousPage ? "" : "none";
    }

    async loadPreviousItems() {
      if (this.isLoading || !this.btnLoadPrevious.dataset.href) return;

      this.setLoading(true);

      const previousHref = this.btnLoadPrevious.dataset.href;

      try {
        const response = await fetch(this.buildRequestUrl(previousHref), {
          credentials: "same-origin",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const page = this.parsePage(await this.readResponseHtml(response));

        if (!page.grid) {
          console.warn(`Dynamic load grid #${this.getGridId()} was not found on ${previousHref}.`);
          this.btnLoadPrevious.dataset.href = "";
          this.toggleButtonVisibility();
          return;
        }

        if (page.itemCount > 0) {
          this.appendContent(page.grid, true);
        }

        this.btnLoadPrevious.dataset.href = page.previousHref || "";
        this.updateUrlFromHref(previousHref);
        this.updateItemCount();
        this.toggleButtonVisibility();
      } catch (error) {
        console.error("Load previous error:", error);
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define("dynamic-load-previous", DynamicLoadPrevious);
}
